import { queryString } from "@/lib/format"
import { translate, translateError, translateValidation } from "@/i18n"
import type {
  AccountRole,
  AccountStatus,
  AccountUsageReport,
  AccountUsageRanking,
  AdminAccountDetail,
  AdminAccountListItem,
  AdminModelView,
  AdminPaymentDetail,
  AdminPaymentListItem,
  AdminUsageRequestDetail,
  ApiKeyCreated,
  ApiKeyView,
  AuditLogView,
  BackgroundJobView,
  GroupedUsageRow,
  InternalAccountNote,
  LoginResult,
  MailOutboxView,
  Money,
  Page,
  PaymentEventView,
  PaymentView,
  PlatformOverview,
  ProblemDetails,
  PublicConfig,
  PublicModel,
  Query,
  RevenueSeries,
  RotatedApiKey,
  SystemHealth,
  UsageInterval,
  UsageRequestView,
  UsageSeries,
  UsageSummary,
  UserView,
  WalletEntryView,
  WalletSummary,
  WebsiteSessionView,
} from "@/types/api"

const MUTATING_METHODS = new Set(["POST", "PUT", "PATCH", "DELETE"])

export class ApiError extends Error {
  readonly problem: ProblemDetails

  constructor(problem: ProblemDetails) {
    super(problem.detail || problem.title)
    this.name = "ApiError"
    this.problem = problem
  }

  get code(): string {
    return this.problem.code
  }

  get fieldErrors(): Record<string, string[]> {
    return this.problem.fieldErrors ?? {}
  }
}

interface RequestOptions {
  csrf?: boolean
  idempotencyKey?: string
  signal?: AbortSignal
  headers?: HeadersInit
}

function fallbackProblem(status: number, path: string, detail: string): ProblemDetails {
  return {
    type: "about:blank",
    title: status === 0 ? "Network request failed" : "Request failed",
    status,
    detail,
    instance: path,
    code: status === 0 ? "NETWORK_ERROR" : "HTTP_ERROR",
    traceId: "unavailable",
  }
}

export class ApiClient {
  private readonly baseUrl: string
  private csrfToken: string | null = null
  private csrfExpiresAt = 0
  private csrfRequest: Promise<string> | null = null
  private unauthorizedHandler: (() => void) | null = null

  constructor(baseUrl = import.meta.env.VITE_MANAGEMENT_API_BASE_URL ?? "") {
    this.baseUrl = baseUrl.replace(/\/$/, "")
  }

  onUnauthorized(handler: (() => void) | null): void {
    this.unauthorizedHandler = handler
  }

  clearSessionSecurityState(): void {
    this.csrfToken = null
    this.csrfExpiresAt = 0
    this.csrfRequest = null
  }

  async get<T>(path: string, query?: Query, options?: RequestOptions): Promise<T> {
    return this.request<T>("GET", `${path}${queryString(query)}`, undefined, options)
  }

  async post<T>(path: string, body?: unknown, options?: RequestOptions): Promise<T> {
    return this.request<T>("POST", path, body, options)
  }

  async patch<T>(path: string, body?: unknown, options?: RequestOptions): Promise<T> {
    return this.request<T>("PATCH", path, body, options)
  }

  async delete<T>(path: string, body?: unknown, options?: RequestOptions): Promise<T> {
    return this.request<T>("DELETE", path, body, options)
  }

  private async fetchCsrf(): Promise<string> {
    if (this.csrfToken && Date.now() < this.csrfExpiresAt - 5000) return this.csrfToken
    this.csrfToken = null
    if (this.csrfRequest) return this.csrfRequest
    this.csrfRequest = this.request<{ token: string; expiresAt: string }>(
      "GET",
      "/api/v1/auth/csrf",
      undefined,
      { csrf: false },
    ).then(({ token, expiresAt }) => {
      this.csrfToken = token
      this.csrfExpiresAt = new Date(expiresAt).getTime()
      return token
    }).finally(() => {
      this.csrfRequest = null
    })
    return this.csrfRequest
  }

  private async request<T>(method: string, path: string, body?: unknown, options: RequestOptions = {}): Promise<T> {
    const headers = new Headers(options.headers)
    headers.set("Accept", "application/json, application/problem+json")
    if (body !== undefined) headers.set("Content-Type", "application/json")
    if (options.idempotencyKey) headers.set("Idempotency-Key", options.idempotencyKey)
    if (MUTATING_METHODS.has(method) && options.csrf !== false) headers.set("X-CSRF-Token", await this.fetchCsrf())

    let response: Response
    try {
      response = await fetch(`${this.baseUrl}${path}`, {
        method,
        headers,
        credentials: "include",
        body: body === undefined ? undefined : JSON.stringify(body),
        signal: options.signal,
      })
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") throw error
      throw new ApiError(fallbackProblem(0, path, "The management API could not be reached."))
    }

    if (response.status === 401) {
      this.clearSessionSecurityState()
      this.unauthorizedHandler?.()
    }
    if (!response.ok) {
      const text = await response.text()
      let problem = fallbackProblem(response.status, path, text || response.statusText)
      try {
        problem = { ...problem, ...(JSON.parse(text) as Partial<ProblemDetails>) }
      } catch {
        // Keep a safe generic representation for non-conforming upstream/proxy failures.
      }
      if (problem.code === "CSRF_INVALID") {
        this.csrfToken = null
        this.csrfExpiresAt = 0
      }
      throw new ApiError(problem)
    }
    if (response.status === 204) return undefined as T
    const text = await response.text()
    return (text ? JSON.parse(text) : undefined) as T
  }
}

export const http = new ApiClient()

const publicMutation = { csrf: false } as const

export const api = {
  public: {
    config: () => http.get<PublicConfig>("/api/v1/public/config"),
    models: (activeOnly = true) => http.get<PublicModel[]>("/api/v1/public/models", { activeOnly }),
  },
  auth: {
    register: (body: { email: string; password: string; displayName: string; timezone: string; acceptedTerms: true }) =>
      http.post<{ verificationRequired: true }>("/api/v1/auth/register", body, publicMutation),
    resendVerification: (email: string) =>
      http.post<{ accepted: true }>("/api/v1/auth/email-verification/resend", { email }, publicMutation),
    confirmVerification: (token: string) =>
      http.post<void>("/api/v1/auth/email-verification/confirm", { token }, publicMutation),
    login: (email: string, password: string) =>
      http.post<LoginResult>("/api/v1/auth/login", { email, password }, publicMutation),
    loginMfa: (challengeToken: string, code: string) =>
      http.post<LoginResult>("/api/v1/auth/login/mfa", { challengeToken, code }, publicMutation),
    session: () => http.get<LoginResult>("/api/v1/auth/session"),
    logout: () => http.post<void>("/api/v1/auth/logout"),
    logoutAll: () => http.post<void>("/api/v1/auth/logout-all"),
    sessions: () => http.get<WebsiteSessionView[]>("/api/v1/auth/sessions"),
    revokeSession: (id: string) => http.delete<void>(`/api/v1/auth/sessions/${id}`),
    forgotPassword: (email: string) =>
      http.post<{ accepted: true }>("/api/v1/auth/password/forgot", { email }, publicMutation),
    resetPassword: (token: string, newPassword: string) =>
      http.post<void>("/api/v1/auth/password/reset", { token, newPassword }, publicMutation),
    changePassword: (currentPassword: string, newPassword: string) =>
      http.post<void>("/api/v1/auth/password/change", { currentPassword, newPassword }),
    setupTotp: (currentPassword: string) => http.post<{ secret: string; otpauthUri: string; expiresAt: string }>(
      "/api/v1/auth/mfa/totp/setup",
      { currentPassword },
    ),
    confirmTotp: (code: string) => http.post<{ enabled: true; recoveryCodes: string[] }>(
      "/api/v1/auth/mfa/totp/confirm",
      { code },
    ),
    disableTotp: (currentPassword: string, code: string) =>
      http.post<void>("/api/v1/auth/mfa/totp/disable", { currentPassword, code }),
  },
  account: {
    get: () => http.get<UserView>("/api/v1/account"),
    update: (body: { displayName?: string; timezone?: string }) => http.patch<UserView>("/api/v1/account", body),
    requestDeletion: (currentPassword: string, reason?: string) => http.post<{ status: "DELETION_PENDING" }>(
      "/api/v1/account/deletion-request",
      { currentPassword, reason },
    ),
  },
  wallet: {
    summary: () => http.get<WalletSummary>("/api/v1/wallet"),
    entries: (query?: Query) => http.get<Page<WalletEntryView>>("/api/v1/wallet/entries", query),
  },
  payments: {
    create: (body: { provider: "CRYPTO_PAY"; amount: Money }, idempotencyKey: string) =>
      http.post<PaymentView>("/api/v1/payments", body, { idempotencyKey }),
    list: (query?: Query) => http.get<Page<PaymentView>>("/api/v1/payments", query),
    get: (id: string) => http.get<PaymentView>(`/api/v1/payments/${id}`),
    cancel: (id: string, idempotencyKey: string) =>
      http.post<PaymentView>(`/api/v1/payments/${id}/cancel`, undefined, { idempotencyKey }),
  },
  keys: {
    list: () => http.get<ApiKeyView[]>("/api/v1/api-keys"),
    create: (body: ApiKeyMutation, idempotencyKey: string) =>
      http.post<ApiKeyCreated>("/api/v1/api-keys", body, { idempotencyKey }),
    update: (id: string, body: Partial<ApiKeyMutation>) => http.patch<ApiKeyView>(`/api/v1/api-keys/${id}`, body),
    rotate: (id: string, idempotencyKey: string) =>
      http.post<RotatedApiKey>(`/api/v1/api-keys/${id}/rotate`, undefined, { idempotencyKey }),
    revoke: (id: string, idempotencyKey: string) =>
      http.delete<void>(`/api/v1/api-keys/${id}`, undefined, { idempotencyKey }),
    usage: (id: string, query?: Query) => http.get<UsageSummary>(`/api/v1/api-keys/${id}/usage`, query),
  },
  usage: {
    summary: (query?: Query) => http.get<UsageSummary>("/api/v1/usage/summary", query),
    report: (query?: Query) => http.get<AccountUsageReport>("/api/v1/usage/report", query),
    series: (query?: Query) => http.get<UsageSeries>("/api/v1/usage/series", query),
    requests: (query?: Query) => http.get<Page<UsageRequestView>>("/api/v1/usage/requests", query),
    request: (id: string) => http.get<UsageRequestView>(`/api/v1/usage/requests/${id}`),
  },
  admin: {
    statistics: {
      overview: (query?: Query) => http.get<PlatformOverview>("/api/v1/admin/statistics/overview", query),
      usage: (query?: Query) => http.get<UsageSeries>("/api/v1/admin/statistics/usage", query),
      revenue: (query?: Query) => http.get<RevenueSeries>("/api/v1/admin/statistics/revenue", query),
      models: (query?: Query) => http.get<GroupedUsageRow[]>("/api/v1/admin/statistics/models", query),
      accounts: (query?: Query) => http.get<AccountUsageRanking[]>("/api/v1/admin/statistics/accounts", query),
    },
    accounts: {
      list: (query?: Query) => http.get<Page<AdminAccountListItem>>("/api/v1/admin/accounts", query),
      get: (id: string) => http.get<AdminAccountDetail>(`/api/v1/admin/accounts/${id}`),
      update: (id: string, body: Record<string, unknown>) => http.patch<AdminAccountDetail>(`/api/v1/admin/accounts/${id}`, body),
      status: (id: string, body: { status: AccountStatus; reason: string; revokeWebsiteSessions: boolean; revokeApiKeys: boolean }) =>
        http.patch<AdminAccountDetail>(`/api/v1/admin/accounts/${id}/status`, body),
      limits: (id: string, body: { rpmLimit: number | null; tpmLimit: number | null; maxParallelRequests: number | null;
        allowedModels: string[]; reason: string }) =>
        http.patch<AdminAccountDetail>(`/api/v1/admin/accounts/${id}/limits`, body),
      role: (id: string, role: AccountRole, reason: string) =>
        http.patch<AdminAccountDetail>(`/api/v1/admin/accounts/${id}/role`, { role, reason }),
      adjustCredit: (id: string, body: CreditAdjustment, idempotencyKey: string) => http.post<{
        entry: WalletEntryView; wallet: WalletSummary; budgetSyncStatus: "PENDING" | "SYNCED" | "FAILED"
      }>(`/api/v1/admin/accounts/${id}/credit-adjustments`, body, { idempotencyKey }),
      passwordReset: (id: string, body: { delivery: "EMAIL" | "RETURN_ONE_TIME_LINK"; revokeSessionsImmediately: boolean;
        reason: string }) => http.post<{ delivery: "EMAIL" | "RETURN_ONE_TIME_LINK"; expiresAt: string; resetUrl: string | null }>(
        `/api/v1/admin/accounts/${id}/password-reset`,
        body,
      ),
      sessions: (id: string) => http.get<WebsiteSessionView[]>(`/api/v1/admin/accounts/${id}/sessions`),
      revokeSessions: (id: string, body: { sessionIds: string[]; all: boolean; reason: string }) =>
        http.post<void>(`/api/v1/admin/accounts/${id}/sessions/revoke`, body),
      keys: (id: string) => http.get<ApiKeyView[]>(`/api/v1/admin/accounts/${id}/api-keys`),
      revokeKey: (id: string, keyId: string, reason: string, idempotencyKey: string) =>
        http.delete<void>(`/api/v1/admin/accounts/${id}/api-keys/${keyId}`, { reason }, { idempotencyKey }),
      usage: (id: string, query?: Query) => http.get<UsageSummary>(`/api/v1/admin/accounts/${id}/usage`, query),
      usageReport: (id: string, query?: Query) =>
        http.get<AccountUsageReport>(`/api/v1/admin/accounts/${id}/usage/report`, query),
      payments: (id: string, query?: Query) => http.get<Page<PaymentView>>(`/api/v1/admin/accounts/${id}/payments`, query),
      wallet: (id: string, query?: Query) => http.get<Page<WalletEntryView>>(`/api/v1/admin/accounts/${id}/wallet/entries`, query),
      notes: (id: string, query?: Query) => http.get<Page<InternalAccountNote>>(`/api/v1/admin/accounts/${id}/notes`, query),
      addNote: (id: string, text: string) => http.post<InternalAccountNote>(`/api/v1/admin/accounts/${id}/notes`, { text }),
      reconcile: (id: string, targets: string[], reason: string, idempotencyKey: string) => http.post<{
        jobIds: string[]; status: "PENDING"
      }>(`/api/v1/admin/accounts/${id}/reconcile`, { targets, reason }, { idempotencyKey }),
    },
    payments: {
      list: (query?: Query) => http.get<Page<AdminPaymentListItem>>("/api/v1/admin/payments", query),
      get: (id: string) => http.get<AdminPaymentDetail>(`/api/v1/admin/payments/${id}`),
      reconcile: (id: string, reason: string, idempotencyKey: string) => http.post<{ jobId: string; status: "PENDING" }>(
        `/api/v1/admin/payments/${id}/reconcile`,
        { reason },
        { idempotencyKey },
      ),
      settle: (id: string, body: { settledAmount: Money; providerReference: string;
        confirmation: "I_UNDERSTAND_THIS_CREATES_CREDIT"; reason: string }, idempotencyKey: string) =>
        http.post<AdminPaymentDetail>(`/api/v1/admin/payments/${id}/manual-settlement`, body, { idempotencyKey }),
      events: (query?: Query) => http.get<Page<PaymentEventView>>("/api/v1/admin/payment-events", query),
    },
    models: {
      list: () => http.get<AdminModelView[]>("/api/v1/admin/models"),
      sync: (reason: string, idempotencyKey: string) => http.post<{ jobId: string; status: "PENDING" }>(
        "/api/v1/admin/models/synchronize",
        { reason },
        { idempotencyKey },
      ),
      update: (id: string, body: Record<string, unknown>) => http.patch<AdminModelView>(`/api/v1/admin/models/${id}`, body),
    },
    usage: {
      requests: (query?: Query) => http.get<Page<UsageRequestView>>("/api/v1/admin/usage/requests", query),
      request: (id: string) => http.get<AdminUsageRequestDetail>(`/api/v1/admin/usage/requests/${id}`),
    },
    audit: {
      list: (query?: Query) => http.get<Page<AuditLogView>>("/api/v1/admin/audit-log", query),
      get: (id: string) => http.get<AuditLogView>(`/api/v1/admin/audit-log/${id}`),
    },
    jobs: {
      list: (query?: Query) => http.get<Page<BackgroundJobView>>("/api/v1/admin/jobs", query),
      get: (id: string) => http.get<BackgroundJobView>(`/api/v1/admin/jobs/${id}`),
      retry: (id: string, reason: string, idempotencyKey: string) => http.post<BackgroundJobView>(
        `/api/v1/admin/jobs/${id}/retry`,
        { reason },
        { idempotencyKey },
      ),
    },
    mail: {
      list: (query?: Query) => http.get<Page<MailOutboxView>>("/api/v1/admin/mail", query),
      retry: (id: string, reason: string, idempotencyKey: string) => http.post<MailOutboxView>(
        `/api/v1/admin/mail/${id}/retry`,
        { reason },
        { idempotencyKey },
      ),
    },
    systemHealth: () => http.get<SystemHealth>("/api/v1/admin/system/health"),
  },
}

export interface ApiKeyMutation {
  alias: string
  allowedModels: string[]
  rpmLimit: number | null
  tpmLimit: number | null
  maxParallelRequests: number | null
  expiresAt: string | null
}

export interface CreditAdjustment {
  direction: "CREDIT" | "DEBIT"
  amount: Money
  category: "ADMIN_ADJUSTMENT" | "SUPPORT_COMPENSATION" | "PROMOTION" | "CHARGEBACK" | "CORRECTION"
  reason: string
}

export function fieldMessage(error: unknown, field: string): string | null {
  if (!(error instanceof ApiError)) return null
  const code = error.fieldErrors[field]?.[0]
  return code ? translateValidation(code) : null
}

export function errorMessage(error: unknown): string {
  if (error instanceof ApiError) return `${translateError(error.code, error.problem.detail)} (${error.code})`
  if (error instanceof Error) return translate(error.message)
  return translate("An unexpected error occurred.")
}

export function defaultUsageQuery(interval: UsageInterval = "DAY"): Query {
  const to = new Date().toISOString()
  const fromDate = new Date()
  fromDate.setUTCDate(fromDate.getUTCDate() - 7)
  return { from: fromDate.toISOString(), to, timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC", interval }
}
